/**
 * Penyelesaian retur sesudah ada tagihan — K197 butir 3 (BUSINESS-27, vonis Owner 25 Sep 2026):
 * "penyelesaian (potong tagihan / refund / barang pengganti) DIPILIH PER RETUR oleh Admin Purchasing,
 *  DISETUJUI Kepala Cabang." Tombol mengikuti peran; pagar sesungguhnya di backend.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { formatIDR } from "../../lib/format";
import UangKembaliStatus from "../../components/etams/UangKembaliStatus";
import {
  ajukanPenyelesaianRetur, putusPenyelesaianRetur, listInvoices, apiErrorMessage,
} from "../../lib/purchasingApi";

const PILIHAN = [
  ["POTONG_TAGIHAN", "Potong tagihan"],
  ["REFUND", "Refund (vendor mengembalikan uang)"],
  ["BARANG_PENGGANTI", "Barang pengganti"],
];
const LABEL = Object.fromEntries(PILIHAN);
const LABEL_STATUS = { MENUNGGU_PERSETUJUAN: "Menunggu persetujuan Kepala Cabang", DISETUJUI: "Disetujui", DITOLAK: "Ditolak" };

export default function PenyelesaianRetur({ retur, onBerubah }) {
  const { roleCode } = useAuth();
  const [pilihan, setPilihan] = useState("POTONG_TAGIHAN");
  const [invoiceId, setInvoiceId] = useState("");
  const [catatan, setCatatan] = useState("");
  const [alasan, setAlasan] = useState("");
  const [tagihan, setTagihan] = useState([]);
  const [busy, setBusy] = useState(false);
  const bolehPilih = roleCode === "ADMIN_PURCHASING" && retur.status === "RETURNED"
    && !["MENUNGGU_PERSETUJUAN", "DISETUJUI"].includes(retur.penyelesaian_status);
  const bolehPutus = roleCode === "KEPALA_CABANG" && retur.penyelesaian_status === "MENUNGGU_PERSETUJUAN";

  useEffect(() => {
    if (!bolehPilih || !retur.po_id) return;
    listInvoices({ po_id: retur.po_id, page_size: 50 })
      .then(({ data }) => setTagihan((data?.items || data || []).filter(t => ["UNPAID", "PARTIAL_PAID"].includes(t.status))))
      .catch((e) => { setTagihan([]); toast.error(apiErrorMessage(e, "Daftar tagihan PO gagal dimuat.")); });
  }, [bolehPilih, retur.po_id]);

  const ajukan = async () => {
    setBusy(true);
    try {
      await ajukanPenyelesaianRetur(retur.id, {
        penyelesaian: pilihan, invoice_id: pilihan === "POTONG_TAGIHAN" ? invoiceId || null : null,
        catatan: catatan.trim() || null,
      });
      toast.success("Penyelesaian diajukan ke Kepala Cabang.");
      onBerubah?.();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  const putus = async (setuju) => {
    setBusy(true);
    try {
      await putusPenyelesaianRetur(retur.id, { setuju, alasan: setuju ? null : alasan.trim() });
      toast.success(setuju ? "Penyelesaian disetujui." : "Penyelesaian ditolak.");
      onBerubah?.();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2 rounded-md border p-3" data-testid="ret-penyelesaian">
      <div className="font-medium">Penyelesaian sesudah ada tagihan</div>
      {retur.penyelesaian ? (
        <div className="text-sm" data-testid="ret-penyelesaian-status">
          {LABEL[retur.penyelesaian]} · {formatIDR(retur.penyelesaian_nilai_idr)} · {LABEL_STATUS[retur.penyelesaian_status]}
          {retur.penyelesaian_alasan_tolak ? <div className="text-muted-foreground">Alasan ditolak: {retur.penyelesaian_alasan_tolak}</div> : null}
          {retur.penyelesaian_catatan ? <div className="text-muted-foreground">Catatan: {retur.penyelesaian_catatan}</div> : null}
          <UangKembaliStatus uangKembali={retur.uang_kembali} testId="ret-uang-kembali" />
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Belum dipilih. Dipilih Admin Purchasing bila PO retur ini sudah punya tagihan.</p>
      )}
      {bolehPilih && (
        <div className="space-y-2 text-sm">
          <select className="h-9 w-full rounded-md border bg-background px-2" value={pilihan}
                  onChange={e => setPilihan(e.target.value)} data-testid="ret-penyelesaian-pilih">
            {PILIHAN.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {pilihan === "POTONG_TAGIHAN" && (
            <select className="h-9 w-full rounded-md border bg-background px-2" value={invoiceId}
                    onChange={e => setInvoiceId(e.target.value)} data-testid="ret-penyelesaian-invoice">
              <option value="">— pilih tagihan yang dipotong —</option>
              {tagihan.map(t => <option key={t.id} value={t.id}>{t.internal_number} · total {formatIDR(t.total_amount)}</option>)}
            </select>
          )}
          <Textarea rows={2} placeholder="Catatan (opsional)" value={catatan} onChange={e => setCatatan(e.target.value)} />
          <Button size="sm" disabled={busy || (pilihan === "POTONG_TAGIHAN" && !invoiceId)} onClick={ajukan}
                  data-testid="ret-penyelesaian-ajukan">Ajukan ke Kepala Cabang</Button>
        </div>
      )}
      {bolehPutus && (
        <div className="space-y-2 text-sm">
          <Textarea rows={2} placeholder="Alasan bila ditolak (min. 5 karakter)" value={alasan}
                    onChange={e => setAlasan(e.target.value)} />
          <div className="flex gap-2">
            <Button size="sm" disabled={busy} onClick={() => putus(true)} data-testid="ret-penyelesaian-setuju">Setujui</Button>
            <Button size="sm" variant="outline" disabled={busy || alasan.trim().length < 5} onClick={() => putus(false)}
                    data-testid="ret-penyelesaian-tolak">Tolak</Button>
          </div>
        </div>
      )}
    </div>
  );
}
